import { Link, useNavigate, useLocation } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import CommonSetting from "../CommonSetting/CommonSetting";
import { setSelectedMod } from "../../Redux/Actions/settingsActions";
import "./CommonHeader.scss";
import { useDispatch, useSelector } from "react-redux";
import LogoutButton from "../../components/Auth/LogoutButton";
import { canViewModule } from "../../Utils/permissions";
import Loading from "../../components/Loading";
import SelectedHotelChip from "./SelectedHotelChip";
import { getRecentNotificationsRequest } from "../../Redux/Actions/Dashboard/DashboardAction";
import { useHotel } from "../HotelContext";

const menuItems = [
  {
    label: "Reservations",
    links: [
      {
        to: "/reservation/booking-list",
        label: "Manage Reservations",
      },
    ],
    resource: "reservation",
  },
  {
    label: "Front Desk",
    links: [
      { to: "/front-desk/dashboard", label: "Dashboard" },
      { to: "/front-desk/guest-management", label: "Guest Management" },
    ],
    resource: "front_desk",
  },
  {
    label: "Room Management",
    links: [
      { to: "/rooms/room-list", label: "Room List" },
      { to: "/rooms/types", label: "Room Types" },
      { to: "/rooms/offers", label: "Offers" },
      { to: "/rooms/packages", label: "Packages" },
      { to: "/rooms/amenity", label: "Amenity" },
    ],
    resource: "rooms",
  },
  {
    label: "Restaurant",
    links: [
      { to: "/restaurant/category", label: "Categories" },
      { to: "/restaurant/table-list", label: "Table List" },
      { to: "/restaurant/order", label: "Orders" },
    ],
    resource: "restaurant",
  },
  {
    label: "Inventory",
    links: [
      { to: "/departments", label: "Departments" },
      { to: "/procurements", label: "Procurements" },
    ],
    resource: "inventory",
  },
  {
    label: "Reports",
    links: [{ to: "/reports", label: "All Reports" }],
    resource: "reports",
  },
  {
    label: "Settings",
    links: [
      { to: "/settings/manageAccess", label: "Manage Access" },
      { to: "/settings/create-user", label: "Create User" },
      { to: "/settings/night-audit", label: "Night Audit" },
      { to: "/settings/budget", label: "Budget Manager" },
      { to: "/settings/custom-expense", label: "Custom Expense Manager" },
      { to: "/settings/restaurant-room-config", label: "Restaurant & Room Settings" },
    ],
    resource: "setting",
  },
  {
    label: "Super Admin",
    resource: "create_admin",
    links: [
      { to: "/create-admin", label: "Create Hotel Admin" },
      { to: "/manage-hotels", label: "Manage Hotels" }
    ],
  },
];

const DropdownMenu = ({ label, links, disabled }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = (e) => {
    e.preventDefault();
    if (!disabled) setOpen((prev) => !prev);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const role = localStorage.getItem('role');
  return (
    <li className="dropdown position-relative" ref={dropdownRef}>
      <a
        href="#"
        role="button"
        aria-expanded={open}
        onClick={toggleDropdown}
        className={`nav-link dropdown-toggle fw-semibold ${disabled ? "text-muted" : "text-secondary"
          }`}
        style={disabled ? { pointerEvents: "none", opacity: 0.5 } : {}}
      >
        {label}
      </a>

      {open && !disabled && (
        <ul className="dropdown-menu show shadow rounded-3 mt-2 animate-dropdown">
          {links.
          filter(({ to }) => {
            if (role === 'housekeeping') {
              return to === '/rooms/room-list'; // show only room-list
            }
            return true; // show all links for other roles
          }).
          map(({ to, label }, index) => (
            <li key={index}>
              <Link
                className="dropdown-item py-2 px-3"
                to={to}
                onClick={() => setOpen(false)} // close on click
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const CommonHeader = ({ toggleSidebar }) => {
  const dispatch = useDispatch();
  const [selectedMode, setSelectedMode] = useState("light");
  const [permissionsReady, setPermissionsReady] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Use HotelContext
  const { selectedHotelName } = useHotel();
  const { bookingSuccess } = useSelector((state) => state.roomReservationList);
  const { procumentSuccess } = useSelector((state) => state.procurement);
  const { recentNotifications, } = useSelector(state => state.dashBoard);
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);

  // Fetch notifications on mount
  useEffect(() => {
    const role = localStorage.getItem("role");
    if (role === "admin") {
      dispatch(getRecentNotificationsRequest());
    }
  }, [dispatch, bookingSuccess, procumentSuccess]);
  // Select notifications from Redux
  const notificationsCount = recentNotifications?.data?.length || recentNotifications?.length || 0;

  // Handle dropdown open/close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const disabledSections = [""];

  const setThemeMode = (mode) => {
    setSelectedMode(mode);
  };

  useEffect(() => {
    dispatch(setSelectedMod(selectedMode));
  }, [selectedMode]);

  useEffect(() => {
    // Poll for permissions in localStorage
    const interval = setInterval(() => {
      if (localStorage.getItem("checkPermission")) {
        setPermissionsReady(true);
        clearInterval(interval);
      }
    }, 100); // check every 100ms

    // Optional: timeout after 10s
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setPermissionsReady(true); // fail-safe: allow render after timeout
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (permissionsReady) {
      if (!canViewModule("dashboard")) {
        // Find first module user can view
        const firstAllowed = menuItems.find(
          (item) => item.resource && canViewModule(item.resource)
        );
        console.log('ffff', firstAllowed)
        if (firstAllowed && firstAllowed.links && firstAllowed.links[0]) {
          navigate(firstAllowed.links[0].to, { replace: true });
        } else {
          // No allowed modules, stay on dashboard
          // Optionally, show a message or handle as you wish
        }
      }
    }
  }, [permissionsReady,  isAuthenticated]);

  if (!permissionsReady) {
    // Show a loading page
    return <Loading />;
  }

  return (
    <>
      <header className="main-header bg-white shadow-sm px-xl-5 px-lg-4 px-3 sticky-top border-bottom">
        <div className="d-flex align-items-center justify-content-between w-100">
          {/* Left: Brand */}
          <div className="d-flex align-items-center">
            <Link
              to="/"
              className="brand-icon d-flex align-items-center text-decoration-none"
            >
              {/* <span className="fw-bold fs-4 text-accent">HMS</span> */}
              <span className="fw-bold fs-4 text-accent">
                {selectedHotelName && localStorage.getItem("role") === "admin"
                  ? selectedHotelName
                  : "HMS"}
              </span>
            </Link>
          </div>

          {/* Middle: Nav with flex-grow */}
          <div className="flex-grow-1 d-flex justify-content-center">
            <ul className="nav gap-1">
              <li className="nav-item">
                {canViewModule("dashboard") && (
                  <Link
                    to="/"
                    className="nav-link fw-semibold text-secondary ml-lg-1"
                  >
                    Dashboard
                  </Link>
                )}
              </li>
              {menuItems.map(
                ({ label, links, resource }, idx) =>
                  (!resource || canViewModule(resource)) && (
                    <DropdownMenu
                      key={idx}
                      label={label}
                      links={links}
                      disabled={disabledSections.includes(label)}
                    />
                  )
              )}
            </ul>
          </div>
          {/* Right: Logout */}
          <div className="d-flex align-items-center gap-3">
            {localStorage.getItem("hotel_name") &&
              localStorage.getItem("role") === "admin" && (
                <>
                  <div
                    style={{
                      display: "inline-block",
                      position: "relative",
                      marginLeft: 0,
                    }}
                    ref={dropdownRef}
                  >
                    <div
                      onClick={() => setDropdownOpen((prev) => !prev)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        background: "#f5f5f5",
                        borderRadius: "50%",
                        width: 36,
                        height: 36,
                        justifyContent: "center",
                        position: "relative",
                        boxShadow: dropdownOpen ? "0 0 0 2px #007bff" : "none",
                      }}
                      aria-label="Notifications"
                    >
                      <span
                        role="img"
                        aria-label="bell"
                        style={{ fontSize: 20 }}
                      >
                        🔔
                      </span>
                      {notificationsCount > 0 && (
                        <span
                          style={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            background: "red",
                            color: "white",
                            borderRadius: "50%",
                            width: 18,
                            height: 18,
                            fontSize: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                          }}
                        >
                          {notificationsCount}
                        </span>
                      )}
                    </div>
                    {dropdownOpen && (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            top: 38,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 0,
                            height: 0,
                            borderLeft: "10px solid transparent",
                            borderRight: "10px solid transparent",
                            borderBottom: "10px solid #fff",
                            zIndex: 1001,
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: 48,
                            left: "50%",
                            transform: "translateX(-50%)",
                            minWidth: 340,
                            maxWidth: 400,
                            maxHeight: 420,
                            overflowY: "auto",
                            background: "white",
                            border: "1px solid #e3e6ef",
                            borderRadius: 16,
                            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                            zIndex: 1000,
                            padding: 0,
                          }}
                        >
                          <div
                            style={{
                              padding: "12px 20px",
                              borderBottom: "1px solid #eee",
                              fontWeight: "bold",
                              background:
                                "linear-gradient(90deg, #007bff 0%, #00c6ff 100%)",
                              color: "white",
                              borderTopLeftRadius: 16,
                              borderTopRightRadius: 16,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>Notifications</span>
                          </div>
                          {(recentNotifications?.data || recentNotifications)
                            ?.length === 0 ? (
                            <div
                              style={{
                                padding: 24,
                                color: "#888",
                                textAlign: "center",
                              }}
                            >
                              No notifications
                            </div>
                          ) : (
                            (
                              recentNotifications?.data || recentNotifications
                            )?.map((notif) => (
                              <div
                                key={notif.id}
                                style={{
                                  padding: "14px 20px",
                                  borderBottom: "1px solid #f5f5f5",
                                  fontSize: 15,
                                  background: notif.read ? "#f8f9fa" : "white",
                                  cursor: "pointer",
                                  transition: "background 0.2s",
                                }}
                                onMouseOver={(e) =>
                                  (e.currentTarget.style.background = "#f0f6ff")
                                }
                                onMouseOut={(e) =>
                                (e.currentTarget.style.background = notif.read
                                  ? "#f8f9fa"
                                  : "white")
                                }
                              >
                                {(() => {
                                  try {
                                    const parsedMessage = JSON.parse(notif.message);
                                    let link = '/';

                                    if (notif.type === 'room_price_mismatch') {
                                      link = `/reservation/add-room-booking/${parsedMessage.url}?isViewOnly=true`;
                                    } else if (notif.type === 'procurement_request') {
                                      link = `/procurements`;
                                    } else {
                                      link = parsedMessage.url || '/';
                                    }
                                    return (
                                      <div style={{ fontWeight: 500, color: notif.read ? '#888' : '#222' }}>
                                        <Link
                                          to={link}
                                          style={{ textDecoration: 'none', color: 'inherit' }}
                                        >
                                          {parsedMessage.message}
                                        </Link>
                                      </div>
                                    );
                                  } catch (e) {
                                    return <div>{notif.message}</div>; // fallback
                                  }
                                })()}
                                <div
                                  style={{
                                    color: "#888",
                                    fontSize: 12,
                                    marginTop: 2,
                                  }}
                                >
                                  {new Date(notif.created_at).toLocaleString()}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {/* <SelectedHotelChip hotelName={localStorage.getItem('hotel_name')|| ''} className="ms-1" />*/}
                </>
              )}
            <LogoutButton />
          </div>
        </div>
      </header>
      <CommonSetting selectedMode={selectedMode} />
    </>
  );
};

export default CommonHeader;
